
import { Component, Prop, Vue } from "vue-property-decorator";

import GET_USER_PROFILE from "@/apollo/profile/GetUserProfile.gql";
import TOGGLE_AVAILABLE from "@/apollo/profile/ToggleAvailable.gql";
import TOGGLE_MARKETPLACE from "@/apollo/profile/ToggleMarketplace.gql";

import PageHeadContainer from "@/components/page/PageHeadContainer.vue";
import PageHeadIcon from "@/components/page/PageHeadIcon.vue";
import BadgeIcon from "@/components/icons/BadgeIcon.vue";
import ClockIcon from "@/components/icons/ClockIcon.vue";
import ApartmentsIcon from "@/components/icons/ApartmentsIcon.vue";
import CrowdIcon from "@/components/icons/CrowdIcon.vue";
import CompaniesIcon from "@/components/icons/CompaniesIcon.vue";
import ProfileBasic from "@/components/profile/ProfileBasic.vue";
import ProfileWebsite from "@/components/profile/ProfileWebsite.vue";
import ProfileDemoReel from "@/components/profile/ProfileDemoReel.vue";
import FormToggle from "@/components/form/FormToggleNew.vue";
import ProfileDetail from "@/components/profile/ProfileDetail.vue";
import ProfileWorkSamples from "@/components/profile/ProfileWorkSamples.vue";
import ProfileResume from "@/components/profile/ProfileResume.vue";
import ProfileExperience from "@/components/profile/experiences/ProfileExperience.vue";
import ProfileEducation from "@/components/profile/educations/ProfileEducation.vue";
import ProfileCertification from "@/components/profile/certifications/ProfileCertification.vue";
import LoadingOverlay from "@/components/common/LoadingOverlay.vue";

import { NOTIFICATION } from "@/entities/Events";
import Message from '@/data/notificationsMessages';
import { User } from "@/generated/graphql";

@Component({
  components: {
    PageHeadContainer,
    PageHeadIcon,
    BadgeIcon,
    ClockIcon,
    ApartmentsIcon,
    CrowdIcon,
    FormToggle,
    ProfileBasic,
    ProfileDetail,
    ProfileDemoReel,
    ProfileWebsite,
    ProfileWorkSamples,
    ProfileResume,
    ProfileExperience,
    ProfileEducation,
    ProfileCertification,
    LoadingOverlay,
    CompaniesIcon,
  },
  apollo: {
    currentUserInfo: {
      query: GET_USER_PROFILE,
      variables() {
        return {
          id: this.userId,
        };
      },
      update: (data) => {
        return data.user_by_pk;
      },
    },
  },
})
export default class ContractorProfile extends Vue {
  @Prop({ type: String, required: true }) readonly userId!: string;

  currentUserInfo?: User = undefined;

  get title(): string {
    if (!this.currentUserInfo) {
      return "n/a";
    }

    const { given_name, family_name } = this.currentUserInfo;
    return `${given_name} ${family_name}`;
  }

  get availability(): string {
    return this.currentUserInfo?.available ? "Available" : "Unavailable"
  }

  get companyName(): string {
    return this.currentUserInfo?.profile?.company_name || "";
  }

  get updatedAt(): string {
    return `last modified ${this.$moment(this.currentUserInfo?.updated_at as string)
      .format("hh:mmA, MMMM DD, YYYY")}`;
  }

  get iconClasses(): string {
    return "mr-1 h-5 w-5 text-gray-400 group-hover:text-gray-500 group-focus:text-gray-500";
  }

  async toggleAvailable(): Promise<void> {
    if (!this.currentUserInfo) {
      return;
    }

    try {
      await this.$apollo.mutate({
        mutation: TOGGLE_AVAILABLE,
        variables: {
          userId: this.userId,
          _set: {
            available: !this.currentUserInfo.available,
          },
        },
      });

      const message = this.currentUserInfo.available
        ? "You status had been changed to 'Available'"
        : "You status had been changed to 'Unavailable'";

      this.$eventBus.emit(
        NOTIFICATION,
        Message.successMessage(message)
      );
    } catch (e) {
      this.$eventBus.emit(
        NOTIFICATION, 
        Message.errorMessage(e.message)
      );
    }
  }

  async toggleMarketplace(): Promise<void> {
    if (!this.currentUserInfo) {
      return;
    }

    try {
      await this.$apollo.mutate({
        mutation: TOGGLE_MARKETPLACE,
        variables: {
          userId: this.userId,
          _set: {
            marketplace: !this.currentUserInfo.marketplace,
          },
        },
      });

      const message = !this.currentUserInfo.marketplace
        ? "You are now not available in the marketplace"
        : "You are now available in the marketplace";

      this.$eventBus.emit(
        NOTIFICATION,
        Message.successMessage(message)
      );
    } catch (e) {
      this.$eventBus.emit(
        NOTIFICATION, 
        Message.errorMessage(e.message)
      );
    }
  }

  updateDate(): void {
    this.currentUserInfo!.updated_at = new Date().toISOString();
  }
}
